import * as React from 'react'
import { lazy } from 'react'
// fix(MG-3600): lazy chunk loading between deployments fallback
// each import fallbacks into a function that reload the app if the chunk is not found
// it could happen after a deployment while the user hasn't reloaded its page
const CHUNK_RELOAD_COUNT_KEY = '__mg_chunk_reload_count'
const MAX_CHUNK_RELOAD_ATTEMPTS = 2
const chunkFailFallback = (error: unknown): Promise<{ default: React.ComponentType<any> }> => {
  const reloadCount = Number(sessionStorage.getItem(CHUNK_RELOAD_COUNT_KEY) ?? 0)
  if (reloadCount < MAX_CHUNK_RELOAD_ATTEMPTS) {
    sessionStorage.setItem(CHUNK_RELOAD_COUNT_KEY, String(reloadCount + 1))
    window.location.reload()
    return new Promise(() => {})
  }
  sessionStorage.removeItem(CHUNK_RELOAD_COUNT_KEY)
  // After max retries, reject so that the ErrorBoundary or Suspense can handle it
  return Promise.reject(error)
}

// Clear the reload key on a successful module load and return the module
const clearChunkReloadKey = <T>(module: T): T => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_COUNT_KEY)
  } catch (e) {
    // ignore sessionStorage errors
  }
  return module
}

// Helper that wraps a dynamic import factory so that failures go through chunkFailFallback
// and successes clean the reload key.
const safeLazy = <T extends { default: React.ComponentType<any> }>(factory: () => Promise<T>) =>
  lazy(() => factory().catch(chunkFailFallback).then(clearChunkReloadKey))

const EventsPiloting = safeLazy(() => import('pages/events/events-piloting'))
const GroupsCreation = safeLazy(() => import('pages/groups-creation/groups-creation'))
const Templates = safeLazy(() => import('pages/templates/templates'))
const UserPreferences = safeLazy(() => import('pages/user-preferences/user-preferences'))
const AnnualInitializationPiloting = safeLazy(
  () => import('pages/sport-piloting/annual-initialization-piloting/annual-initialization-piloting-by-groups-of-sports')
)
const AnnualInitializationTeamsPiloting = safeLazy(
  () => import('pages/sport-piloting/annual-initialization-piloting/annual-initialization-piloting-by-teams')
)
const ComparisonSportGroups = safeLazy(() => import('pages/sport-piloting/comparison/comparison-by-sport-group'))
const ComparisonSports = safeLazy(() => import('pages/sport-piloting/comparison/comparison-by-sports'))
const ComparisonStores = safeLazy(() => import('pages/sport-piloting/comparison/comparison-by-stores'))
const ComparisonTeams = safeLazy(() => import('pages/sport-piloting/comparison/comparison-by-teams'))
const DailyPilotingStore = safeLazy(() => import('pages/sport-piloting/daily-piloting/daily-piloting-store'))
const DailyPilotingTeam = safeLazy(() => import('pages/sport-piloting/daily-piloting/daily-piloting-team'))
const SportsPiloting = safeLazy(
  () => import('pages/sport-piloting/groups-of-sports-piloting/groups-of-sports-piloting-by-groups-of-sports')
)
const SportsStoresPiloting = safeLazy(
  () => import('pages/sport-piloting/groups-of-sports-piloting/groups-of-sports-piloting-by-stores')
)
const SportsTeamsPiloting = safeLazy(
  () => import('pages/sport-piloting/groups-of-sports-piloting/groups-of-sports-piloting-by-teams')
)
const ManagerByMonth = safeLazy(() => import('pages/sport-piloting/monthly-manager-piloting'))
const WeeklySportsGroupsPiloting = safeLazy(
  () => import('pages/sport-piloting/weekly-piloting/weekly-piloting-by-groups-of-sports')
)
const WeeklyPiloting = safeLazy(() => import('pages/sport-piloting/weekly-piloting/weekly-piloting-by-sports'))
const WeeklyStoresPiloting = safeLazy(() => import('pages/sport-piloting/weekly-piloting/weekly-piloting-by-stores'))
const WeeklyTeamsPiloting = safeLazy(() => import('pages/sport-piloting/weekly-piloting/weekly-piloting-by-teams'))
const Planning = safeLazy(() => import('pages/planning/page'))
const DatavizWeekly = safeLazy(() => import('pages/dataviz-weekly/dataviz-weekly'))
const DatavizComparison = safeLazy(() => import('pages/dataviz-comparison/dataviz-comparison'))

const ReportingUsageByCountryIFrame = safeLazy(() => import('pages/reporting-usage/reporting-usage-by-country'))
const ReportingHoursEfficiencyPerCountryIFrame = safeLazy(
  () => import('pages/reporting-hours/reporting-hours-efficiency-per-country')
)
const ReportingHoursEfficiencyPerCountryProbableYearIFrame = safeLazy(
  () => import('pages/reporting-hours/reporting-hours-efficiency-per-country-probable-year')
)
const ReportingQualityForecastingIFrame = safeLazy(
  () => import('pages/reporting-quality-forecasting/ReportingQualityForecasting')
)
const ReportingEffitimeReliabilityIFrame = safeLazy(
  () => import('pages/reporting-effitime/ReportingEffitimeReliabilityIFrame')
)
const EffitimeIframe = safeLazy(() => import('pages/effitime/effitime'))
const SalesPerformancesIframe = safeLazy(() => import('pages/sales-performances/sales-performances'))

const ReportingExtraction = safeLazy(() => import('pages/reporting-extraction/reporting-extraction'))
const ReportingExtractionMonthlyData = safeLazy(
  () => import('pages/reporting-extraction/reporting-extraction-monthly-data')
)
const ReportingExtractionDayDay = safeLazy(() => import('pages/reporting-extraction/reporting-extraction-dayday'))
const ReportingExtractionTrajectory = safeLazy(
  () => import('pages/reporting-extraction/reporting-extraction-trajectory')
)
const ReportingExtractionWeekly = safeLazy(() => import('pages/reporting-extraction/reporting-extraction-weekly'))

const DayByDayHourByHourEmbeded = safeLazy(() => import('pages/extended-analysis/dayday-hourhour'))
const EmbeddedWorkshopPerformance = safeLazy(() => import('pages/extended-analysis/workshop-performance'))
const EmbeddedRentalPerformance = safeLazy(() => import('pages/extended-analysis/rental-performance'))
const EmbeddedDataExtraction = safeLazy(() => import('pages/extended-analysis/data-extraction'))

const StoreTrajectories = safeLazy(() => import('pages/trajectory/store/store-trajectories'))
const StoreTrajectoryEdit = safeLazy(() => import('../../../pages/trajectory/store/edit/store-trajectory-edit'))

const ErrorPage = safeLazy(() => import('pages/error/error'))
const Announcement = safeLazy(() => import('pages/announcement/announcement'))
const HoursCommercial = safeLazy(() => import('pages/hours/hours-piloting-commercial'))
const HoursCashier = safeLazy(() => import('pages/hours/hours-piloting-cashier'))
const HoursGeneralServices = safeLazy(() => import('pages/hours/hours-piloting-general-services'))

export {
  EventsPiloting,
  Templates,
  UserPreferences,
  AnnualInitializationPiloting,
  AnnualInitializationTeamsPiloting,
  ComparisonSportGroups,
  ComparisonSports,
  ComparisonStores,
  ComparisonTeams,
  DailyPilotingStore,
  DailyPilotingTeam,
  SportsPiloting,
  SportsStoresPiloting,
  SportsTeamsPiloting,
  ManagerByMonth,
  WeeklySportsGroupsPiloting,
  WeeklyPiloting,
  WeeklyStoresPiloting,
  WeeklyTeamsPiloting,
  DatavizWeekly,
  DatavizComparison,
  GroupsCreation,
  Planning,
  ReportingUsageByCountryIFrame,
  ReportingHoursEfficiencyPerCountryIFrame,
  ReportingHoursEfficiencyPerCountryProbableYearIFrame,
  ReportingQualityForecastingIFrame,
  ReportingEffitimeReliabilityIFrame,
  EffitimeIframe,
  SalesPerformancesIframe,
  ReportingExtraction,
  ReportingExtractionMonthlyData,
  ReportingExtractionDayDay,
  ReportingExtractionTrajectory,
  ReportingExtractionWeekly,
  DayByDayHourByHourEmbeded,
  Announcement,
  HoursCommercial,
  HoursCashier,
  HoursGeneralServices,
  ErrorPage,
  EmbeddedWorkshopPerformance,
  EmbeddedRentalPerformance,
  EmbeddedDataExtraction,
  StoreTrajectories,
  StoreTrajectoryEdit
}
